<i18n>
{
	"en": {
		"addFilter":                      "Add Filter",
		"advancedSearch":                 "Advanced Search",
		"allLocations":                   "All Locations",
		"completed":                      "Completed",
		"confirmOrderRemovalTransporter": "An order you want to remove has already been sent. You will need to contact the transporter to complete the removal. Are you sure you want to remove the selected orders?",
		"confirmOrderRemovalTrip":        "Are you sure you want to remove the selected order from trip {tripNumber}?",
		"editSearch":                     "Edit search",
		"fromDate":                       "From",
		"fromLocation":                   "From Location",
		"loading":                        "Loading...",
		"locations":                      "Locations",
		"openInLoadPlanning":             "Open in load planning",
		"ready":                          "Ready",
		"resetSearch":                    "Reset Search",
		"save":                           "Save",
		"saveSearch":                     "Save this search",
		"search":                         "Search",
		"searchForLocation":              "Search for location",
		"searchForTrips":                 "Trips",
		"selectDate":                     "Select date",
		"selectTripStatus":               "Select status",
		"status":                         "Status",
		"toDate":                         "To",
		"toLocation":                     "To Location",
		"tripStatusSelected":             "All statuses | {count} status selected | {count} statuses selected",
		"underway":                       "Underway",
		"cancelled": "Cancelled",
		"draft": "Draft",
		"received": "Received",
		"rejected": "Rejected",
		"started": "Started",
		"requested": "Requested",
		"accepted": "Accepted",
		"unloaded": "Unloaded",
		"loaded": "Loaded",
		"planned": "Planned",
		"unsuccessful": "Unsuccessful pickup",
		"readyForPickup": "Ready for pickup",
		"selectTripsFromArea": "Select trips from area",
		"selectTripsToArea": "Select trips to area",
		"failedToFetch": "Failed to search for trips"
	},
	"no": {
		"addFilter":                      "Legg til filter",
		"advancedSearch":                 "Avansert søk",
		"allLocations":                   "Alle steder",
		"completed":                      "Fullført",
		"confirmOrderRemovalTransporter": "Oppdraget du vil fjerne, er allerede sendt. Du må kontakte transportøren for å fullføre fjerningen. Er du sikker på at du vil fjerne de valgte oppdrag?",
		"confirmOrderRemovalTrip":        "Er du sikker på at du vil fjerne den valgte oppdrag fra turen {tripNumber}?",
		"editSearch":                     "Rediger dette søket",
		"fromDate":                       "Fra",
		"fromLocation":                   "Fra sted",
		"loading":                        "Henter turer...",
		"locations":                      "Steder",
		"openInLoadPlanning":             "Åpne i lastetorg",
		"ready":                          "Klar",
		"resetSearch":                    "Nullstill søk",
		"save":                           "Lagre",
		"saveSearch":                     "Lagre dette søket",
		"search":                         "Søk",
		"searchForLocation":              "Søk etter sted",
		"searchForTrips":                 "Turer",
		"selectDate":                     "Velg dato",
		"selectTripStatus":               "Velg status",
		"status":                         "Status",
		"toDate":                         "Til",
		"toLocation":                     "Til sted",
		"tripStatusSelected":             "Alle statuser | {count} status valgt | {count} statuser valgt",
		"underway":                       "Underveis",
		"cancelled": "Annullert",
		"draft": "Kladd",
		"received": "Mottatt",
		"rejected": "Avslått",
		"started": "Startet",
		"requested": "Forespurt",
		"accepted": "Godtatt",
		"unloaded": "Losset",
		"loaded": "Lastet",
		"planned": "Planlagt",
		"unsuccessful": "Bomtur",
		"readyForPickup": "Klar for lasting",
		"selectTripsFromArea": "Velg turer fra område",
		"selectTripsToArea": "Velg turer til område",
		"failedToFetch": "Kunne ikke søke etter turer"
	}
}
</i18n>
<template>
	<t-vertical-panel class="trip-search" data-droppable="orderSearch">
		<div slot="header" class="trip-search-header">
			<TripSearchFiltersHeader
				:items="savedSearches"
				:selectedItem="loadedSearch"
				width="100%"
				@change="onLoadSavedSearch"
				@toggleFilters="$emit('toggleFilters')"
			>
				<template>
					{{ loadedSearch.id === -1 ? $t('searchForTrips') : loadedSearch.label}}
				</template>

				<template v-slot:menuitemlabel="{item}">
					{{ item.labelKey ? $t(item.labelKey) : item.label }}
				</template>
			</TripSearchFiltersHeader>
		</div>

		<div slot="sub-header" class="trip-search-sub-header">
			<t-button
				:disabled="tripsLoading"
				appearance="primary"
				width="100%"
				@click="searchTrips"
			>
				{{tripSearchText}}
			</t-button>
			<t-icon-button
				appearance="minimal"
				width="100%"
				type="bp"
				icon="filter-keep"
				@click="$emit('saveSearch')"
			/>
		</div>

		<div class="trip-search-content">
			<t-form-group>
				<t-date-picker
					ref="firstInput"
					:label="$t('fromDate')"
					:value="formatDate(searchFilter.etd.from)"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateFrom"
					@enter="searchTrips"
				/>
			</t-form-group>
			<t-form-group>
				<t-date-picker
					:label="$t('toDate')"
					:value="formatDate(searchFilter.etd.to)"
					:placeholder="$t('selectDate')"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateTo"
					@enter="searchTrips"
				/>
			</t-form-group>

			<t-form-group>
				<LocationsCombobox
					ref="fromLocation"
					:label="$t('fromLocation')"
					:placeholder="$t('searchForLocation')"
					:selected="searchFilter.fromLocation"
					:dialogTitle="$t('selectTripsFromArea')"
					disableGlobalAddress
					dialogType="fromLocation"
					width="100%"
					iconSize="sm"
					icon="geosearch"
					@input="inputs.fromLocation = $event"
					@select="onSelectFromLocation"
					@multiselect="setFromLocation"
					@enter="searchTrips"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.fromLocation)"
					:items="searchFilter.fromLocation"
					displayKey="label"
					@change="setFromLocation"
					@tag-click="changeTagOpCode({ key: 'fromLocation', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="label" />
					</template>
				</t-tag-container>
			</t-form-group>

			<t-form-group>
				<LocationsCombobox
					ref="toLocation"
					:label="$t('toLocation')"
					:placeholder="$t('allLocations')"
					:selected="searchFilter.toLocation"
					:dialogTitle="$t('selectTripsToArea')"
					disableGlobalAddress
					dialogType="toLocation"
					width="100%"
					iconSize="sm"
					icon="geosearch"
					@input="inputs.toLocation = $event"
					@select="onSelectToLocation"
					@multiselect="setToLocation"
					@enter="searchTrips"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.toLocation)"
					:items="searchFilter.toLocation"
					displayKey="label"
					@change="setToLocation"
					@tag-click="changeTagOpCode({ key: 'toLocation', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="label" />
					</template>
				</t-tag-container>
			</t-form-group>


			<t-form-group>
				<trip-types-select :selectedItems="searchFilter.tripType" @change="addTripType"	/>

				<t-tag-container
					v-if="hasItems(searchFilter.tripType)"
					:items="searchFilter.tripType"
					@change="setTripType"
				>
					<template v-slot="{item}">
						{{ item.value }}
					</template>
				</t-tag-container>
			</t-form-group>

			<OperationalStatusFormInput
				:items="statuses"
				:selectedItems="searchFilter.status"
				relative
				@change="addStatus"
			>
				<template v-slot:displayedValue="{popoverShow}">
					{{ popoverShow ? $t('selectTripStatus') : $tc('tripStatusSelected', searchFilter.status.length)}}
				</template>
				<template #after>
					<t-tag-container
						v-if="hasItems(searchFilter.status)"
						:items="searchFilter.status"
						@change="setStatuses"
					>
						<template v-slot="{item}">
							<div style="display: flex; align-items: center; justify-content: flex-start;">
								<t-status-color-dot :color="getStatusColor(item)"/>
								<div style="margin-left: 5px;">{{getStatusText(item)}}</div>
							</div>
						</template>
					</t-tag-container>
				</template>
			</OperationalStatusFormInput>

			<t-form-group>
				<cargo-carriers-search-input
					ref="cargoCarrier"
					:selectedItems="searchFilter.cargoCarrier"
					width="100%"
					@input="inputs.cargoCarrier = $event"
					@change="addCargoCarrierString"
					@select="addCargoCarrier"
					@enter="searchTrips"
					@set:selected="setCargoCarrier"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.cargoCarrier)"
					:items="searchFilter.cargoCarrier"
					displayKey="description"
					@change="setCargoCarrier"
					@tag-click="changeTagOpCodeBetweenNotAndOr({ key: 'cargoCarrier', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="description" />
					</template>
				</t-tag-container>
			</t-form-group>

			<t-form-group :title="$t('advancedSearch')">
				<t-button type="bp" iconBefore="add" appearance="minimal">{{$t('addFilter')}}</t-button>
			</t-form-group>
		</div>
	</t-vertical-panel>
</template>

<script>
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, statuses, displayDateFormat, displayDateFormatter
} from '@/core';
import LocationsCombobox from '@/modules/locations/LocationsCombobox';
import TripTypesSelect from '@/modules/tripTypes/TripTypesSelect';
import CargoCarriersSearchInput from '@/modules/cargoCarriers/CargoCarriersSearchInput';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput';
import TripSearchFiltersHeader from '@/modules/loadPlanningList/TripSearch/TripSearchFiltersHeader';
import { mapState as savedSearchState } from '@/modules/saveLoadPlanningSearch/store/saveLoadPlanningSearch.store';

export default {
	name: 'TripSearchFilters',
	components: {
		LocationsCombobox,
		TripTypesSelect,
		CargoCarriersSearchInput,
		OperationalStatusFormInput,
		TripSearchFiltersHeader,
	},
	inject: ['namespace'],
	data() {
		return {
			inputs: {
				fromLocation: '',
				toLocation: '',
				cargoCarrier: ''
			}
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace',[
			'searchFilter',
			'tripsLoading',
			'loadedSearch',
		]),
		...savedSearchState(['savedSearches']),

		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}/tripSearch`;
		},
		headerSelectItems() {
			return [{ id: -1, label: this.$t('resetSearch') }];
		},
		tripSearchText() {
			return this.tripsLoading ? this.$t('loading') : this.$t('search');
		},
		displayDateFormat() {
			return displayDateFormat;
		},
		statuses() {
			return Object.values(statuses.trip).filter(x => x > 0 && x < 70).map(x => ({ id:x }));
		}
	},
	created() {
		eventBus.$on('refreshTripList', this.performFetchTrips);
	},
	mounted() {
		document.addEventListener('keyup', this.onKeyup, { passive: true, capture: true });
	},
	beforeDestroy() {
		document.removeEventListener('keyup', this.onKeyup, { capture: true });
		eventBus.$off('refreshTripList');
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace',[
			'fetchTrips',
			'loadSavedSearch',
			'setDateFrom',
			'setDateTo',
			'addFromLocation',
			'addFromLocationString',
			'setFromLocation',
			'addToLocation',
			'addToLocationString',
			'setToLocation',
			'addStatus',
			'setStatuses',
			'addTripType',
			'setTripType',
			'setCargoCarrier',
			'addCargoCarrier',
			'addCargoCarrierString',
			'addInputTagsOnSearch',
			'changeTagOpCode',
			'changeTagOpCodeBetweenNotAndOr',
		]),
		searchTrips() {
			this.addInputTagsOnSearch(this.inputs);
			this.$refs.fromLocation.clearInput();
			this.$refs.toLocation.clearInput();
			this.$refs.cargoCarrier.clearInput();
			this.performFetchTrips();
		},
		async performFetchTrips() {
			try {
				await this.fetchTrips();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},
		hasItems(array) {
			return array.length > 0;
		},
		onSelectFromLocation(location) {
			
			if (typeof location === 'string') {
				this.addFromLocationString(location);
				
			} else {
				this.addFromLocation(location);
			}
		},
		onSelectToLocation(location) {
			
			if (typeof location === 'string') {
				this.addToLocationString(location);
				
			} else {
				this.addToLocation(location);
			}
		},
		onLoadSavedSearch(item) {
			if (!item) return;
			let mapView = this.$router.currentRoute.name.search('Map') > 0;
			if (item.id === -1) {
				this.$router.push({
					name: mapView ? 'loadPlanningMap' : 'loadPlanningList',
				});
			} else {
				this.$router.push({
					name: mapView ? 'savedLoadPlanningMap' : 'savedLoadPlanningList',
					params: { searchId: item.id }
				});
			}
		},
		getStatusText(status) {
			return this.$t(statuses.operational(status));
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		formatDate(date) {
			return displayDateFormatter(date);
		},
		onKeyup(e) {
			if (e.altKey && e.key === '2') this.$refs.firstInput.focus();
		}
	}
};
</script>


<style lang="scss" scoped>
.trip-search {
	background: white;
	&-header {
		display:flex;
		align-items: center;
		height: 100%;
	}

	&-sub-header {
		display:inline-grid;
		grid-auto-flow: column;
		grid-auto-columns: 1fr;
		column-gap: 20px;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
	}

	&-content {
		display: grid;
		grid-auto-rows: minmax(min-content, max-content);
		row-gap: 20px;
		background: white;
		min-height: 100%;
		box-sizing: border-box;
		padding: 10px 15px 10px 15px;
	}
}
</style>
